import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	UserRoles,
	TLanguageIDs,
	IGetSubcategoryInfoV2,
	IGetSubcategoryRestaurantProperties,
	IGetSubcategoryRestaurantPropertiesArguments,
} from 'types';
import { viewSubcategoryInitialState, ViewSubcategoryContext, TTypeOfModalWindow } from './viewSubcategoryContext';
import { viewSubcategoryReducer } from './viewSubcategoryReducer';
import { ViewSubcategoryActionTypes } from './viewSubcategoryActions';
import { useNamedHeader } from 'hooks/useNamedHeader';
import { useFetchData } from 'hooks/useFetchData';
import { getSubcategoryInfoV2, getSubcategoryRestaurantProperties } from 'backend/subcategories';
import { useRoleAccess } from 'hooks/useRoleAccess';

export const ViewSubcategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(viewSubcategoryReducer, viewSubcategoryInitialState);
	const { showLoader, hideLoader, loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const params = new URL(document.location.href).searchParams;
	const subcategoryID = params.get('subcategoryID');

	const {
		response: subcategoryFullInfoResponse,
		loading: subcategoryFullInfoLoading,
		refetch: subcategoryFullInfoRefetch,
	} = useFetchData<IGetSubcategoryInfoV2, string>(subcategoryID ? getSubcategoryInfoV2 : null, subcategoryID || '');

	const {
		response: restaurantPropertiesResponse,
		loading: restaurantPropertiesLoading,
		refetch: restaurantPropertiesRefetch,
	} = useFetchData<IGetSubcategoryRestaurantProperties, IGetSubcategoryRestaurantPropertiesArguments>(
		state.restaurantIdForRestaurantProps && state.subcategoryFullInfo?.id
			? getSubcategoryRestaurantProperties
			: null,
		{
			restaurantId: state.restaurantIdForRestaurantProps || '',
			subcategoryId: state.subcategoryFullInfo?.id || '',
		},
	);

	useNamedHeader(subcategoryFullInfoResponse?.data.name);

	useEffect(() => {
		subcategoryFullInfoLoading || restaurantPropertiesLoading ? showLoader() : hideLoader();
	}, [subcategoryFullInfoLoading, restaurantPropertiesLoading]);

	useEffect(() => {
		if (subcategoryFullInfoResponse) {
			const userRestaurantIDs = loggedUser.restaurants
				? loggedUser.restaurants.map((restaurant) => restaurant.id)
				: [];

			const accessibleRestaurants = hasAccess
				? subcategoryFullInfoResponse.data.restaurants
				: subcategoryFullInfoResponse.data.restaurants.filter((restaurant) =>
						userRestaurantIDs.includes(restaurant.id),
				  );

			dispatch({
				type: ViewSubcategoryActionTypes.SET_SUBCATEGORY_FULL_INFO,
				payload: {
					subcategoryFullInfo: subcategoryFullInfoResponse.data,
					accessibleRestaurants,
				},
			});
		}
	}, [loggedUser.restaurants, hasAccess, subcategoryFullInfoResponse]);

	useEffect(() => {
		if (restaurantPropertiesResponse) {
			dispatch({
				type: ViewSubcategoryActionTypes.SET_RESTAURANT_PROPS,
				payload: {
					restaurantProps: restaurantPropertiesResponse.data,
				},
			});
		}
	}, [restaurantPropertiesResponse]);

	useEffect(() => {
		if (state.needRefetchData) {
			subcategoryFullInfoRefetch();
			dispatch({
				type: ViewSubcategoryActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		restaurantPropertiesRefetch();
	}, [state.restaurantIdForRestaurantProps]);

	useEffect(() => {
		if (state.needRefetchRestPropsData) {
			restaurantPropertiesRefetch();
			dispatch({
				type: ViewSubcategoryActionTypes.REFETCH_REST_PROPS_DATA,
				payload: {
					needRefetchRestPropsData: false,
				},
			});
		}
	}, [state.needRefetchRestPropsData]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: ViewSubcategoryActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const refetchRestPropsData = useCallback((value: boolean) => {
		dispatch({
			type: ViewSubcategoryActionTypes.REFETCH_REST_PROPS_DATA,
			payload: {
				needRefetchRestPropsData: value,
			},
		});
	}, []);

	const setRestaurantIdForRestaurantProps = useCallback(
		(value: Nullable<string>) => {
			dispatch({
				type: ViewSubcategoryActionTypes.SET_RESTAURANT_ID_FOR_RESTAURANT_PROPS,
				payload: {
					restaurantIdForRestaurantProps: value,
					restaurantProps: value ? state.restaurantProps : null,
				},
			});
		},
		[state.restaurantProps],
	);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: ViewSubcategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: ViewSubcategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<ViewSubcategoryContext.Provider
			value={{
				refetchData,
				refetchRestPropsData,
				setRestaurantIdForRestaurantProps,
				setTranslationTab,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				needRefetchRestPropsData: state.needRefetchRestPropsData,
				subcategoryFullInfo: state.subcategoryFullInfo,
				accessibleRestaurants: state.accessibleRestaurants,
				restaurantIdForRestaurantProps: state.restaurantIdForRestaurantProps,
				restaurantProps: state.restaurantProps,
				selectedTranslationTab: state.selectedTranslationTab,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ViewSubcategoryContext.Provider>
	);
};
